<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="executive-gender-slope"
    />
    <ChartContent
      height="261px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="slopeChart"
      class="px-0 pl-3"
      :class="{ 'px-3': loading }"
      :link="{ name: 'Gender Report', to: 'gender-report' }"
    >
      <LineChart :id="widgetId" :data="chartData" :options="chartOptions" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import LineChart from '@/components/insights/Chart/LineChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import { executiveInsightsPluginsConfig, executiveInsightsYAxisOptions, executiveInsightsXScaleOptions } from '@/api/helpers/insights/commonChartConfigs';

const MAX_VALUE = 100;

const props = defineProps({
  genderDistribution: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What is the gender distribution of our candidates?';

const widgetId = 'exec-gender-distribution';

const {
  candidateFemaleInferred,
  candidateMaleInferred,
  candidateOther,
  white,
} = useColors();

const noWidgetData = computed(() => Object.values(props.genderDistribution || {})
  .every((value) => !value));

const chartData = computed(() => {
  const {
    maleAppsPercent,
    maleHiredPercent,
    femaleAppsPercent,
    femaleHiredPercent,
    undeterminedAppsPercent,
    undeterminedHiredPercent,
  } = props.genderDistribution || {};
  const datasets = [
    {
      data: [maleAppsPercent, maleHiredPercent],
      label: 'Male',
      borderColor: candidateMaleInferred.value,
      backgroundColor: candidateMaleInferred.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
    {
      data: [femaleAppsPercent, femaleHiredPercent],
      label: 'Female',
      borderColor: candidateFemaleInferred.value,
      backgroundColor: candidateFemaleInferred.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
    {
      data: [undeterminedAppsPercent, undeterminedHiredPercent],
      label: 'Unknown',
      borderColor: candidateOther.value,
      backgroundColor: candidateOther.value,
      borderWidth: 2,
      pointBackgroundColor: white.value,
      pointRadius: 4,
      pointBorderWidth: 3,
      tension: 0,
    },
  ];
  return {
    labels: ['Application', '            Hire'],
    datasets,
  };
});
const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  layout: {
    padding: {
      bottom: 16,
    },
  },
  plugins: executiveInsightsPluginsConfig(chartData, widgetId),
  scales: {
    y: executiveInsightsYAxisOptions(MAX_VALUE),
    y2: {
      position: 'right',
      ...executiveInsightsYAxisOptions(MAX_VALUE),
    },
    x: executiveInsightsXScaleOptions,
  },
}));
</script>

<style lang="less" scoped>
:deep(.chart-error-image) {
  height: 261px !important;
  max-height: 261px !important;
  padding: 50px !important;
  margin: 0 !important;
}
</style>
